import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';

const ChartSettings = ({ onClose, isDarkMode, users = [], currentUserId }) => {
  const [charts, setCharts] = useState({
    companyCount: {
      name: 'Počet firem v CRM',
      max: 1000,
      ranges: [
        { min: 0, max: 200, color: '#ef4444', message: '<name>, koukej makat! 😤' },
        { min: 200, max: 500, color: '#f97316', message: 'Dobrý start, <name>! 💪' },
        { min: 500, max: 800, color: '#eab308', message: 'Skvělá práce, <name>! ⭐' },
        { min: 800, max: 1000, color: '#22c55e', message: 'Jsi borec/ka, <name>! 🎉' }
      ]
    },
    bmslDeals: {
      name: 'Uzavřené smlouvy BMSL',
      max: 20000,
      unit: 'Kč',
      ranges: [
        { 
          min: 0, 
          max: 8000, 
          color: '#ef4444', 
          messages: {
            early: '<name>, koukej zabrat! Začátek měsíce je klíčový! 😬',
            mid: '<name>, ještě můžeš dohnat! Půlka je za tebou! 💪',
            late: '<name>, zabíráš! Ale musíš zabrat víc! ⚡'
          }
        },
        { 
          min: 8000, 
          max: 12000, 
          color: '#f97316', 
          messages: {
            early: 'Dobrý start, <name>! Máš na to! 👍',
            mid: 'Vypadá to dobře, <name>! Pokračuj! 🔥',
            late: 'Slušný výsledek, <name>! Ještě trochu! 💪'
          }
        },
        { 
          min: 12000, 
          max: 20000, 
          color: '#22c55e', 
          messages: {
            early: 'Bomba, <name>! Jsi na top už na začátku! 🚀',
            mid: 'Skvěle, <name>! Udržuj tempo! ⭐',
            late: 'Dokonalý měsíc, <name>! Jsi šampion! 🏆'
          }
        }
      ]
    }
  });

  const [userNicknames, setUserNicknames] = useState({});
  const [userSettings, setUserSettings] = useState({}); // Nastavení graf pro jednotlivé uživatele
  const [selectedChart, setSelectedChart] = useState('companyCount');
  const [activeTab, setActiveTab] = useState('ranges'); // 'ranges' nebo 'users'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
    console.log('ChartSettings - users z props:', users.length);
  }, []);

  const loadSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/chart-settings?userId=${currentUserId || ''}`);
      if (response.ok) {
        const data = await response.json();
        if (data.charts) setCharts(data.charts);
        if (data.userNicknames) setUserNicknames(data.userNicknames);
        if (data.userSettings) setUserSettings(data.userSettings);
      }
    } catch (error) {
      console.error('Chyba při načítání nastavení grafů:', error);
    } finally {
      setLoading(false);
    }
  };



  const saveSettings = async () => {
    try {
      console.log('Ukládám nastavení:', { charts, userNicknames, userSettings });
      const response = await fetch(`${API_URL}/chart-settings?userId=${currentUserId || ''}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ charts, userNicknames, userSettings })
      });

      if (response.ok) {
        const saved = await response.json();
        console.log('Uloženo:', saved);
        alert('Nastavení grafů bylo uloženo! ✅');
      } else {
        alert('Chyba při ukládání nastavení');
      }
    } catch (error) {
      console.error('Chyba při ukládání nastavení grafů:', error);
      alert('Chyba při ukládání nastavení');
    }
  };

  const currentChart = charts[selectedChart];

  const updateRange = (index, field, value) => {
    setCharts(prev => ({
      ...prev,
      [selectedChart]: {
        ...prev[selectedChart],
        ranges: prev[selectedChart].ranges.map((range, i) => 
          i === index ? { ...range, [field]: field === 'min' || field === 'max' ? Number(value) : value } : range
        )
      }
    }));
  };

  const toggleUserChart = (userId, chartKey, enabled) => {
    console.log('Toggle chart:', { userId, chartKey, enabled });
    setUserSettings(prev => {
      const newSettings = {
        ...prev,
        [userId]: {
          ...prev[userId],
          [chartKey]: enabled
        }
      };
      console.log('New userSettings:', newSettings);
      return newSettings;
    });
  };

  const updateUserNickname = (userId, nickname) => {
    setUserNicknames(prev => ({
      ...prev,
      [userId]: nickname
    }));
  };

  const addRange = () => {
    const lastRange = currentChart.ranges[currentChart.ranges.length - 1];
    const newRange = selectedChart === 'bmslDeals' 
      ? { 
          min: lastRange.max, 
          max: lastRange.max + 100, 
          color: '#3b82f6', 
          messages: {
            early: 'Nová zpráva pro začátek měsíce',
            mid: 'Nová zpráva pro střed měsíce',
            late: 'Nová zpráva pro konec měsíce'
          }
        }
      : { 
          min: lastRange.max, 
          max: lastRange.max + 100, 
          color: '#3b82f6', 
          message: 'Nová zpráva pro <name>' 
        };
        
    setCharts(prev => ({
      ...prev,
      [selectedChart]: {
        ...prev[selectedChart],
        ranges: [
          ...prev[selectedChart].ranges,
          newRange
        ]
      }
    }));
  };

  const removeRange = (index) => {
    if (currentChart.ranges.length <= 2) {
      alert('Musí být alespoň 2 rozsahy!');
      return;
    }
    setCharts(prev => ({
      ...prev,
      [selectedChart]: {
        ...prev[selectedChart],
        ranges: prev[selectedChart].ranges.filter((_, i) => i !== index)
      }
    }));
  };

  const generateGradient = () => {
    const ranges = currentChart.ranges;
    const max = currentChart.max;
    
    // Seřaď rozsahy podle min hodnoty
    const sortedRanges = [...ranges].sort((a, b) => a.min - b.min);
    
    if (sortedRanges.length === 0) return 'linear-gradient(90deg, #ccc 0%, #ccc 100%)';
    
    // Vytvoř plynulé přechody mezi barvami s velkou přechodovou zónou
    const gradientStops = [];
    const transitionSize = 20; // 20% přechodová zóna pro velmi hladký přechod
    
    sortedRanges.forEach((range, index) => {
      const startPercent = (range.min / max) * 100;
      const endPercent = (range.max / max) * 100;
      
      if (index === 0) {
        // První rozsah - začíná od své barvy
        gradientStops.push(`${range.color} ${startPercent}%`);
        
        if (sortedRanges.length === 1) {
          // Jediný rozsah
          gradientStops.push(`${range.color} ${endPercent}%`);
        } else {
          // Přechod na další barvu
          const nextRange = sortedRanges[index + 1];
          const transitionStart = Math.max(endPercent - transitionSize, startPercent);
          gradientStops.push(`${range.color} ${transitionStart}%`);
          gradientStops.push(`${nextRange.color} ${endPercent}%`);
        }
      } else if (index === sortedRanges.length - 1) {
        // Poslední rozsah - už má začátek z předchozího
        gradientStops.push(`${range.color} ${endPercent}%`);
      } else {
        // Prostřední rozsahy
        const nextRange = sortedRanges[index + 1];
        const transitionStart = Math.max(endPercent - transitionSize, startPercent);
        gradientStops.push(`${range.color} ${transitionStart}%`);
        gradientStops.push(`${nextRange.color} ${endPercent}%`);
      }
    });
    
    return `linear-gradient(90deg, ${gradientStops.join(', ')})`;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto`}>
        {/* Header */}
        <div className={`sticky top-0 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} p-6 z-10`}>
          <div className="flex justify-between items-center">
            <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              📊 Nastavení grafů
            </h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Záložky */}
          <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex gap-4">
              <button
                onClick={() => {
                  console.log('Přepínám na záložku: ranges');
                  setActiveTab('ranges');
                }}
                className={`px-4 py-2 font-semibold border-b-2 transition ${
                  activeTab === 'ranges'
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                📊 Rozsahy a barvy
              </button>
              <button
                onClick={() => {
                  console.log('Přepínám na záložku: users, počet uživatelů:', users.length);
                  setActiveTab('users');
                }}
                className={`px-4 py-2 font-semibold border-b-2 transition ${
                  activeTab === 'users'
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                👥 Nastavení uživatelů
              </button>
            </div>
          </div>

          {/* Obsah podle aktivní záložky */}
          {activeTab === 'ranges' ? (
            <>
              {/* Výběr grafu */}
              <div className="mb-6">
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Vyberte graf:
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedChart('companyCount')}
                    className={`px-4 py-2 rounded-lg font-semibold transition ${
                      selectedChart === 'companyCount'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300'
                    }`}
                  >
                    📈 Počet firem v CRM
                  </button>
                  <button
                    onClick={() => setSelectedChart('bmslDeals')}
                    className={`px-4 py-2 rounded-lg font-semibold transition ${
                      selectedChart === 'bmslDeals'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300'
                    }`}
                  >
                    💰 Uzavřené smlouvy BMSL
                  </button>
                </div>
              </div>

              {/* Nastavení maxima */}
              <div className="mb-6">
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Maximum grafu:
                </label>
                <input
                  type="number"
                  value={currentChart.max}
                  onChange={(e) => setCharts(prev => ({
                    ...prev,
                    [selectedChart]: { ...prev[selectedChart], max: Number(e.target.value) }
                  }))}
                  className={`px-4 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                />
              </div>

              {/* Náhled gradientu */}
              <div className="mb-6">
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Náhled progress baru (s plynulými přechody):
                </label>
                <div className="h-8 rounded-lg" style={{ background: generateGradient(), opacity: 0.8 }}></div>
              </div>

              {/* Rozsahy */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <label className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Rozsahy a zprávy:
                  </label>
                  <button
                    onClick={addRange}
                    className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold"
                  >
                    + Přidat rozsah
                  </button>
                </div>

                <div className="space-y-3">
                  {currentChart.ranges.map((range, index) => (
                    <div key={index} className={`p-4 border ${isDarkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'} rounded-lg`}>
                      <div className="grid grid-cols-12 gap-3 items-start">
                        <div className="col-span-2">
                          <label className="text-xs text-gray-500 dark:text-gray-400">Min</label>
                          <input
                            type="number"
                            value={range.min}
                            onChange={(e) => updateRange(index, 'min', e.target.value)}
                            className={`w-full px-2 py-1 border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'} text-sm`}
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="text-xs text-gray-500 dark:text-gray-400">Max</label>
                          <input
                            type="number"
                            value={range.max}
                            onChange={(e) => updateRange(index, 'max', e.target.value)}
                            className={`w-full px-2 py-1 border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'} text-sm`}
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="text-xs text-gray-500 dark:text-gray-400">Barva</label>
                          <input
                            type="color"
                            value={range.color}
                            onChange={(e) => updateRange(index, 'color', e.target.value)}
                            className="w-full h-8 rounded cursor-pointer"
                          />
                        </div>
                        
                        {/* Zprávy - pro BMSL graf tři různé podle fáze měsíce, jinak jedna */}
                        {selectedChart === 'bmslDeals' ? (
                          <div className="col-span-5 space-y-2">
                            <div>
                              <label className="text-xs text-gray-500 dark:text-gray-400">Zpráva 1.-10. (použijte &lt;name&gt;)</label>
                              <input
                                type="text"
                                value={range.messages?.early || ''}
                                onChange={(e) => updateRange(index, 'messages', { ...range.messages, early: e.target.value })}
                                placeholder="Zpráva pro začátek měsíce"
                                className={`w-full px-2 py-1 border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'} text-sm`}
                              />
                            </div>
                            <div>
                              <label className="text-xs text-gray-500 dark:text-gray-400">Zpráva 11.-20.</label>
                              <input
                                type="text"
                                value={range.messages?.mid || ''}
                                onChange={(e) => updateRange(index, 'messages', { ...range.messages, mid: e.target.value })}
                                placeholder="Zpráva pro střed měsíce"
                                className={`w-full px-2 py-1 border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'} text-sm`}
                              />
                            </div>
                            <div>
                              <label className="text-xs text-gray-500 dark:text-gray-400">Zpráva 21.-konec</label>
                              <input
                                type="text"
                                value={range.messages?.late || ''}
                                onChange={(e) => updateRange(index, 'messages', { ...range.messages, late: e.target.value })}
                                placeholder="Zpráva pro konec měsíce"
                                className={`w-full px-2 py-1 border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'} text-sm`}
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="col-span-5">
                            <label className="text-xs text-gray-500 dark:text-gray-400">Zpráva (použijte &lt;name&gt;)</label>
                            <input
                              type="text"
                              value={range.message || ''}
                              onChange={(e) => updateRange(index, 'message', e.target.value)}
                              placeholder="Např: <name>, koukej makat!"
                              className={`w-full px-2 py-1 border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'} text-sm`}
                            />
                          </div>
                        )}
                        
                        <div className="col-span-1 flex justify-end">
                          <button
                            onClick={() => removeRange(index)}
                            className="px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs"
                            title="Smazat rozsah"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Příklad zprávy */}
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-50'} mb-6`}>
                <h4 className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                  💡 Příklad zprávy:
                </h4>
                <p className={`text-sm ${isDarkMode ? 'text-blue-200' : 'text-blue-600'}`}>
                  {selectedChart === 'bmslDeals' ? (
                    <>
                      <strong>Graf BMSL má tři zprávy podle fáze měsíce:</strong><br />
                      • 1.-10. den: Zobrazí první zprávu<br />
                      • 11.-20. den: Zobrazí druhou zprávu<br />
                      • 21.-konec: Zobrazí třetí zprávu<br /><br />
                      Pro uživatele "Jan Novák" s přezdívkou "Jane" se &lt;name&gt; nahradí → "Jane"
                    </>
                  ) : (
                    <>
                      Pro uživatele "Jan Novák" s přezdívkou "Jane" a zprávou "&lt;name&gt;, koukej zabrat! 😬" se zobrazí:
                      <br />
                      <strong>"Jane, koukej zabrat! 😬"</strong>
                    </>
                  )}
                </p>
              </div>
            </>
          ) : (
            <>
              {/* Nastavení pro jednotlivé uživatele */}
              <div className="mb-6">
                <label className={`block text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Nastavení grafů pro uživatele:
                </label>
                
                {users.length === 0 ? (
                  <div className={`p-4 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Načítám uživatele...
                  </div>
                ) : (
                  <div className="space-y-3">
                    {users.map(user => {
                      const companyCountEnabled = userSettings[user._id]?.companyCount !== false;
                      const bmslDealsEnabled = userSettings[user._id]?.bmslDeals !== false;
                      const nickname = userNicknames[user._id] || '';
                      
                      return (
                        <div key={user._id} className={`p-4 border ${isDarkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'} rounded-lg`}>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {user.name}
                            </h4>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Přezdívka */}
                            <div>
                              <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">
                                Přezdívka/skloňování
                              </label>
                              <input
                                type="text"
                                value={nickname}
                                onChange={(e) => updateUserNickname(user._id, e.target.value)}
                                placeholder={`Např: ${user.name.split(' ')[0]}o`}
                                className={`w-full px-3 py-2 border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'} text-sm`}
                              />
                            </div>
                            
                            {/* Graf: Počet firem */}
                            <div>
                              <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">
                                Graf: Počet firem v CRM
                              </label>
                              <button
                                onClick={() => toggleUserChart(user._id, 'companyCount', !companyCountEnabled)}
                                className={`w-full px-3 py-2 rounded font-semibold transition ${
                                  companyCountEnabled
                                    ? 'bg-green-600 hover:bg-green-700 text-white'
                                    : 'bg-red-500 hover:bg-red-600 text-white'
                                }`}
                              >
                                {companyCountEnabled ? '✅ Zapnuto' : '❌ Vypnuto'}
                              </button>
                            </div>
                            
                            {/* Graf: BMSL */}
                            <div>
                              <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">
                                Graf: Uzavřené smlouvy BMSL
                              </label>
                              <button
                                onClick={() => toggleUserChart(user._id, 'bmslDeals', !bmslDealsEnabled)}
                                className={`w-full px-3 py-2 rounded font-semibold transition ${
                                  bmslDealsEnabled
                                    ? 'bg-green-600 hover:bg-green-700 text-white'
                                    : 'bg-red-500 hover:bg-red-600 text-white'
                                }`}
                              >
                                {bmslDealsEnabled ? '✅ Zapnuto' : '❌ Vypnuto'}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Tlačítka */}
          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-semibold"
            >
              Zrušit
            </button>
            <button
              onClick={saveSettings}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold"
            >
              💾 Uložit nastavení
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartSettings;
